import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { countdownParts } from "../lib/dropFormat";

/**
 * DropCountdown — segmented countdown chip. Two visual modes:
 *  - hero (large 4-segment grid)
 *  - chip (single inline chip, used on cards + strips)
 *
 * Re-renders once per second via internal interval. No external state needed.
 */
export default function DropCountdown({ iso, mode = "chip", testid }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const c = countdownParts(iso);

  if (!c) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.18em]"
        style={{ background: "rgba(255,255,255,0.05)", color: "var(--text-muted)" }}
        data-testid={testid}>
        Date TBD
      </span>
    );
  }
  if (c.live) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em] border"
        style={{ background: "rgba(79,168,111,0.16)", borderColor: "rgba(127,203,153,0.55)", color: "#7FCB99" }}
        data-testid={testid || "countdown-live"}>
        <span className="live-dot"/> Live now
      </span>
    );
  }

  if (mode === "hero") {
    const segs = [
      { v: c.days,    l: "Days" },
      { v: c.hours,   l: "Hours" },
      { v: c.minutes, l: "Min" },
      { v: c.seconds, l: "Sec" },
    ];
    return (
      <div className="flex gap-2 md:gap-3" data-testid={testid || "countdown-hero"}>
        {segs.map(s => (
          <div key={s.l} className="bg-[var(--surface)] border border-[var(--border-strong)] px-3 md:px-4 py-2 md:py-3 min-w-[60px] md:min-w-[80px] text-center">
            <div className="font-mono text-2xl md:text-4xl tracking-tighter leading-none" style={{ color: "var(--gold)" }}>
              {String(s.v).padStart(2, "0")}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] mt-1" style={{ color: "var(--text-muted)" }}>{s.l}</div>
          </div>
        ))}
      </div>
    );
  }

  const inline = c.days > 0
    ? `${c.days}d ${String(c.hours).padStart(2,"0")}h ${String(c.minutes).padStart(2,"0")}m`
    : `${String(c.hours).padStart(2,"0")}:${String(c.minutes).padStart(2,"0")}:${String(c.seconds).padStart(2,"0")}`;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 border font-mono text-[11px] uppercase tracking-[0.16em]"
      style={{ background: "rgba(212,175,55,0.08)", borderColor: "rgba(212,175,55,0.45)", color: "#D4AF37" }}
      data-testid={testid || "countdown-chip"}>
      <Clock className="w-3 h-3"/>{inline}
    </span>
  );
}
